import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Event } from '../types/Event';
import { API_BASE_URL } from '../config';

interface Props {
  event: Event;
  open: boolean;
  onClose: () => void;
  onDuplicated?: () => void;
}

// Local (not UTC) YYYY-MM-DD, used as the date input's minimum.
const getTodayStr = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DuplicateEventModal: React.FC<Props> = ({ event, open, onClose, onDuplicated }) => {
  const [startDate, setStartDate] = useState('');
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatCount, setRepeatCount] = useState(4);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset inputs each time the dialog opens, suggesting the next week.
  useEffect(() => {
    if (!open) return;
    const today = getTodayStr();
    const suggested = addDays(event.date, 7);
    setStartDate(suggested >= today ? suggested : addDays(today, 7));
    setRepeatWeekly(false);
    setRepeatCount(4);
    setError(null);
  }, [open, event.date]);

  // The dates that will be created given the current inputs.
  const dates = (() => {
    if (!startDate) return [] as string[];
    const count = repeatWeekly ? Math.max(1, Math.min(repeatCount, 52)) : 1;
    return Array.from({ length: count }, (_, i) => addDays(startDate, i * 7));
  })();

  const handleDuplicate = async () => {
    if (dates.length === 0) {
      setError('請選擇開始日期');
      return;
    }
    setIsDuplicating(true);
    setError(null);
    try {
      await axios.post(
        `${API_BASE_URL}/events/${event._id}/duplicate`,
        { dates },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (onDuplicated) onDuplicated();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || '複製活動失敗，請稍後再試。');
    } finally {
      setIsDuplicating(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !isDuplicating && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>複製活動</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          以相同的時間、地點、費用與設定建立新活動（不含參加者）。
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="開始日期"
          type="date"
          fullWidth
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: getTodayStr() }}
          sx={{ mb: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={repeatWeekly}
              onChange={(e) => setRepeatWeekly(e.target.checked)}
              color="primary"
            />
          }
          label="每週重複"
        />
        {repeatWeekly && (
          <TextField
            label="重複次數（週）"
            type="number"
            fullWidth
            value={repeatCount}
            onChange={(e) => setRepeatCount(Math.max(1, Math.min(parseInt(e.target.value) || 1, 52)))}
            inputProps={{ min: 1, max: 52, step: 1 }}
            helperText="每隔 7 天建立一場，最多 52 場"
            sx={{ mt: 1 }}
          />
        )}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            將建立 {dates.length} 場活動
          </Typography>
          <Typography variant="body2">
            {dates.slice(0, 8).join('、')}
            {dates.length > 8 ? ' …' : ''}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDuplicating}>取消</Button>
        <Button onClick={handleDuplicate} variant="contained" disabled={isDuplicating || dates.length === 0}>
          {isDuplicating ? <CircularProgress size={24} /> : '建立'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateEventModal;
